import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ProductId } from "@redotech/redo-product-id";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo, useState } from "react";

type ProductItem = BadgeRedoListItem<ProductId>;

function toItem(product: {
  productId: ProductId;
  title: string;
  imageUrl?: string | null;
}): ProductItem {
  return {
    id: product.productId,
    type: "badge",
    value: product.productId,
    badge: {
      text: product.title,
      color: "gray",
      // `name` seeds the fallback initials when a product has no image.
      avatar: {
        alt: product.title,
        name: product.title,
        imageUrl: product.imageUrl ?? null,
      },
    },
  };
}

/**
 * Multi-select over the Redo supplies catalog. Loads once and filters
 * client-side on title and SKU; the catalog is small and Redo-owned.
 */
export const SuppliesProductSelect = memo(function SuppliesProductSelect({
  productIds,
  setProductIds,
  disabled,
}: {
  productIds: ProductId[];
  setProductIds: (productIds: ProductId[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const client = useRequiredContext(RedoMerchantRpcClientContext);
  const [searchString, setSearchString] = useState("");

  const productsLoad = useLoad(
    async (signal) => {
      const { products } = await client.getSuppliesCatalogProducts(
        {},
        { signal },
      );
      return products;
    },
    [client],
  );

  const products = useMemo(
    () => productsLoad.value ?? [],
    [productsLoad.value],
  );

  const options = useMemo(() => {
    const query = searchString.trim().toLowerCase();
    const matching = query
      ? products.filter(
          (product) =>
            product.title.toLowerCase().includes(query) ||
            product.sku?.toLowerCase().includes(query),
        )
      : products;
    return matching.map(toItem);
  }, [products, searchString]);

  // A saved rule may name a product the catalog no longer returns; show its id
  // rather than silently dropping it from the condition.
  const selectedOptions = useMemo(() => {
    const byId = new Map(products.map((p) => [p.productId, p]));
    return productIds.map((productId) => {
      const product = byId.get(productId);
      return toItem({
        productId,
        title: product?.title ?? productId,
        imageUrl: product?.imageUrl,
      });
    });
  }, [productIds, products]);

  return (
    <RedoTagsDropdownInput
      description={t("Matches when the cart contains any of these products.")}
      disabled={disabled}
      label={t("Products")}
      loading={productsLoad.pending}
      options={options}
      placeholder={t("Select products")}
      searchPlaceholder={t("Search the supplies catalog...")}
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) =>
        setProductIds(items.map((item) => item.value))
      }
      size="xs"
    />
  );
});
